<script setup lang="ts">
  import { IconImage, Tooltip } from '@/shared/ui';
  import { useFormBuilderSidebars } from '../../composables/useFormBuilderSidebars';
  import MenuFriesLeft from '@/shared/assets/icons/MenuFriesLeftIcon.svg';
  import MenuFriesRight from '@/shared/assets/icons/MenuFriesRightIcon.svg';
  import { useFormBuilderStore } from '../../model/form-builder.store';
  import { useTableEditorStore } from '../../model/table-editor.store';
  import type { ElementType, FillTitleProperties as FillTitlePropertiesType } from '../../model/form.types';
  import { getSidebarElements } from '../../lib/element-registry';
  import { createElement } from '../../lib/create-element';
  import {
    countFillTitleElements,
    getNextAvailableFillTitleType,
    MAX_FILL_TITLE_COUNT,
  } from '../../lib/fill-title-utils';
  import { hasIndexerTitleElement } from '../../lib/indexer-title-utils';
  import { useFormBuilderToast } from '../../composables/useFormBuilderToast';

  const { leftCollapsed: isCollapsed, toggleLeft: toggleSidebar } = useFormBuilderSidebars();

  const formBuilderStore = useFormBuilderStore();
  const tableEditorStore = useTableEditorStore();
  const { showErrorToast } = useFormBuilderToast();

  const elements = getSidebarElements();

  function addElementToCanvas(type: ElementType) {
    if (type === 'INDEXER_TITLE' && hasIndexerTitleElement(formBuilderStore.elements)) {
      showErrorToast({
        title: 'Já existe um Título Indexador no formulário.',
        type: 'error',
        hideIcon: true,
      });
      return;
    }

    if (type === 'FILL_TITLE' && countFillTitleElements(formBuilderStore.elements) >= MAX_FILL_TITLE_COUNT) {
      showErrorToast({
        title: 'Número máximo de elementos "Título preenchimento" alcançados.',
        type: 'error',
        hideIcon: true,
      });
      return;
    }

    const newElement = createElement(type);

    if (type === 'FILL_TITLE') {
      const nextType = getNextAvailableFillTitleType(formBuilderStore.elements);
      if (nextType) {
        (newElement.properties as FillTitlePropertiesType).fillTitleType = nextType;
      }
    }

    if (tableEditorStore.isEditingMode && tableEditorStore.selectedCell && type !== 'TABLE') {
      tableEditorStore.insertElementIntoCell(newElement);
    } else {
      formBuilderStore.addElement(newElement);
    }
  }
</script>

<template>
  <aside
    data-testid="element-library-sidebar"
    :class="[
      'h-full bg-[var(--color-white-02)] border-r border-[var(--color-gray-02)] flex flex-col overflow-y-auto transition-all duration-300',
      isCollapsed ? 'w-[72px]' : 'w-[250px]'
    ]"
  >
    <div
      class="p-4 flex items-center"
      :class="[
        isCollapsed ? 'justify-center' : 'justify-start border-b border-[var(--color-gray-02)]'
      ]"
    >
      <button
        class="h-[42px] w-[42px] px-3 border border-[var(--color-border-color)] rounded-lg flex items-center justify-center hover:bg-[var(--hover-primary-color)] transition-colors"
        @click="toggleSidebar"
      >
        <IconImage
          :src="isCollapsed ? MenuFriesRight : MenuFriesLeft"
          alt="Colapsar menu"
          class="w-4"
        />
      </button>
      <h2 v-if="!isCollapsed" class="ml-4 text-sm font-semibold text-[var(--color-gray-07)]">Elementos</h2>
    </div>

    <div class="flex-1 flex flex-col" :class="isCollapsed ? 'p-2 items-center gap-1' : 'py-6 px-4 gap-1'">
      <template v-for="element in elements" :key="element.type">
        <Tooltip
          v-if="isCollapsed"
          :content="element.label"
          placement="right"
        >
          <button
            class="p-2 bg-white-01 border border-[var(--color-gray-02)] rounded-lg hover:border-[var(--theme-secondary-ink)] hover:bg-[var(--hover-primary-color)] transition-all cursor-pointer flex items-center justify-center"
            @click="addElementToCanvas(element.type)"
          >
            <div class="w-6.5 h-6.5 rounded-sm bg-[var(--theme-secondary)] flex items-center justify-center flex-shrink-0">
              <component
                :is="element.icon"
                class="text-[var(--theme-on-secondary)]"
              />
            </div>
          </button>
        </Tooltip>

        <button
          v-else
          class="w-full p-2 bg-white-01 border border-[var(--color-gray-02)] rounded-lg hover:border-[var(--theme-secondary-ink)] hover:bg-[var(--hover-primary-color)] transition-all cursor-pointer group"
          @click="addElementToCanvas(element.type)"
        >
          <div class="flex gap-2">
            <div
              class="w-6.5 h-6.5 rounded-sm bg-[var(--theme-secondary)] flex items-center justify-center flex-shrink-0 transition-colors"
            >
              <component
                :is="element.icon"
                class="text-[var(--theme-on-secondary)]"
              />
            </div>

            <div class="flex-1 text-left flex items-center">
              <h3 class="text-sm  text-[var(--color-gray-07)]">
                {{ element.label }}
              </h3>
            </div>
          </div>
        </button>
      </template>
    </div>
  </aside>
</template>
